import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { AlertCircle, Loader2, Moon, Sun, Sunrise } from 'lucide-react';
import { toast } from 'sonner';

import { ChatComposer } from '@/components/chat/ChatComposer';
import { ChatMessageList } from '@/components/chat/ChatMessageList';
import { OwnerBadge } from '@/components/chat/OwnerBadge';
import { ReadOnlyChatBanner } from '@/components/chat/ReadOnlyChatBanner';
import { ClaudeStatusIndicator } from '@/components/ClaudeStatusIndicator';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type {
  ChatInteraction,
  ChatInteractionSubmission,
  SkillInfo,
} from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { useChatStore } from '@/stores/chatStore';
import { useSettingsStore } from '@/stores/settingsStore';

const SUMMARIZE_THRESHOLD = 100;

const PROVIDER_MODELS: Record<'claude' | 'codex', string[]> = {
  claude: ['fable', 'opus', 'sonnet', 'haiku'],
  codex: ['gpt-5.5', 'gpt-5.4', 'gpt-5.4-mini', 'gpt-5.3-codex-spark'],
};

const PROVIDER_EFFORTS: Record<'claude' | 'codex', string[]> = {
  claude: ['low', 'medium', 'high', 'xhigh', 'max'],
  codex: ['low', 'medium', 'high', 'xhigh'],
};

interface ChatPanelProps {
  className?: string;
  onSendMessage?: (message: string, images?: string[]) => boolean | Promise<boolean>;
  onSubmitInteraction?: (
    interaction: ChatInteraction,
    submission: ChatInteractionSubmission
  ) => Promise<boolean>;
  onCancelStream?: () => void;
  skills?: SkillInfo[];
  onOpenStatus?: () => void;
}

function Greeting() {
  const { user } = useAuthStore();
  const { userName } = useSettingsStore();
  const hour = new Date().getHours();

  let greeting: string;
  let Icon: typeof Sun;
  if (hour >= 5 && hour < 12) {
    greeting = 'Good Morning';
    Icon = Sunrise;
  } else if (hour >= 12 && hour < 17) {
    greeting = 'Good Afternoon';
    Icon = Sun;
  } else {
    greeting = 'Good Evening';
    Icon = Moon;
  }

  const displayName = userName || user?.username;
  const displayGreeting = displayName ? `${greeting}, ${displayName}` : greeting;
  return (
    <div className="flex flex-1 items-start justify-center p-8 pt-16 md:pt-24">
      <div className="max-w-md text-center">
        <Icon className="mx-auto mb-4 h-14 w-14 text-primary-500 dark:text-primary-400 md:h-20 md:w-20" />
        <h2 className="text-base font-semibold text-surface-900 dark:text-surface-100 md:text-lg">{displayGreeting}</h2>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="text-center">
        <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-primary-600" />
        <p className="text-surface-600 dark:text-surface-400">Loading messages...</p>
      </div>
    </div>
  );
}

function ErrorState({ error }: { error: string }) {
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="max-w-md text-center">
        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive-100 text-destructive-600 dark:bg-destructive-900/30 dark:text-destructive-400">
          <AlertCircle className="h-8 w-8" />
        </div>
        <h2 className="mb-2 text-xl font-semibold text-surface-900 dark:text-surface-100">Failed to Load Messages</h2>
        <p className="text-destructive-600 dark:text-destructive-400">{error}</p>
      </div>
    </div>
  );
}

export function ChatPanel({
  className,
  onSendMessage,
  onSubmitInteraction,
  onCancelStream,
  skills = [],
  onOpenStatus,
}: ChatPanelProps) {
  const {
    activeSession,
    activeSessionId,
    sessionLoading,
    sessionError,
    activeStreamsBySession,
    streamsById,
    streamError,
    providerConnectionRequired,
    cancelStreaming,
    clearStreamError,
    clearProviderConnectionRequired,
    updateSessionLlmConfig,
    forkSession,
  } = useChatStore();

  const messages = activeSession?.messages || [];
  const provider = activeSession?.provider === 'codex' ? 'codex' : 'claude';
  const activeSessionStreamId = activeSessionId ? activeStreamsBySession[activeSessionId] : null;
  const activeStream = activeSessionStreamId ? streamsById[activeSessionStreamId] : null;
  const isStreaming = activeStream?.state === 'streaming' || activeStream?.state === 'connecting';
  // Another user's chat is read-only: no composer, no settings, no interactions.
  const isReadOnly = Boolean(activeSession && !activeSession.canPost);
  const configLocked = isReadOnly || messages.some((message) => !message.isStreaming);
  const [isCopying, setIsCopying] = useState(false);

  const handleContinueCopy = useCallback(async () => {
    if (!activeSession) return;
    setIsCopying(true);
    try {
      const copy = await forkSession(activeSession.id);
      if (!copy) toast.error('Unable to copy this chat');
    } finally {
      setIsCopying(false);
    }
  }, [activeSession, forkSession]);
  const activeProviderConnectionRequired =
    providerConnectionRequired?.sessionId === activeSessionId
      ? providerConnectionRequired
      : null;

  const applyLlmConfig = useCallback(
    async (config: { provider?: 'claude' | 'codex'; model?: string | null; reasoningEffort?: string | null }) => {
      if (!activeSession || configLocked) return;
      const ok = await updateSessionLlmConfig(activeSession.id, config);
      if (!ok) toast.error('Unable to update chat model settings');
    },
    [activeSession, configLocked, updateSessionLlmConfig]
  );

  const summarizeToastShownRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!activeSessionId) return;
    if (messages.length < SUMMARIZE_THRESHOLD || summarizeToastShownRef.current.has(activeSessionId)) return;
    summarizeToastShownRef.current.add(activeSessionId);
    toast.info('Long conversation detected', {
      description: 'Run /summarize to create a handoff doc, then continue in a new chat.',
      duration: 10_000,
      action: {
        label: 'Learn more',
        onClick: () => {
          toast.message('How to summarize', {
            description: '1. Type /summarize in the chat\n2. Copy the handoff document\n3. Create a new chat\n4. Paste the document as your first message',
            duration: 15_000,
          });
        },
      },
    });
  }, [activeSessionId, messages.length]);

  const handleSendMessage = useCallback(
    (message: string, images?: string[]) => onSendMessage?.(message, images) ?? false,
    [onSendMessage]
  );
  const handleCancelStream = useCallback(() => {
    cancelStreaming(activeSessionStreamId || undefined);
    onCancelStream?.();
  }, [activeSessionStreamId, cancelStreaming, onCancelStream]);

  return (
    <div className={cn('flex h-full flex-col bg-white dark:bg-surface-950', className)}>
      {activeSession && (
        <div className="flex items-center justify-between border-b border-surface-200 px-4 py-3 dark:border-surface-800">
          <div className="flex items-center gap-3">
            <OwnerBadge ownerUserId={activeSession.ownerUserId} ownerUsername={activeSession.ownerUsername} />
            <h2 className="truncate font-medium text-surface-900 dark:text-surface-100">{activeSession.name}</h2>
            {configLocked ? (
              <>
                {provider === 'claude' ? (
                  <ClaudeStatusIndicator />
                ) : (
                  <span className="rounded-md bg-green-500/10 px-2 py-1 text-xs font-medium text-green-600 dark:text-green-300">Codex</span>
                )}
                {(activeSession.model || activeSession.reasoningEffort) && (
                  <span className="rounded-md bg-primary-500/10 px-2 py-1 text-xs font-medium text-primary-700 dark:text-primary-300" title="LLM settings are fixed for this chat">
                    {[activeSession.model, activeSession.reasoningEffort].filter(Boolean).join(' · ')}
                  </span>
                )}
              </>
            ) : (
              <div className="flex flex-wrap items-center gap-1.5" title="Pick the LLM for this chat. Settings lock after the first message.">
                <div className="flex items-center gap-0.5 rounded-md bg-surface-100 p-0.5 dark:bg-surface-800">
                  {(['claude', 'codex'] as const).map((nextProvider) => (
                    <button
                      key={nextProvider}
                      type="button"
                      onClick={() => void applyLlmConfig({ provider: nextProvider, model: null, reasoningEffort: null })}
                      className={cn(
                        'rounded px-2 py-1 text-xs font-medium transition-colors',
                        provider === nextProvider
                          ? 'bg-primary-500/15 text-primary-700 dark:text-primary-300'
                          : 'text-surface-500 hover:text-surface-800 dark:text-surface-400 dark:hover:text-surface-100'
                      )}
                    >
                      {nextProvider === 'codex' ? 'Codex' : 'Claude'}
                    </button>
                  ))}
                </div>
                <select
                  value={activeSession.model || ''}
                  onChange={(event) => void applyLlmConfig({ model: event.target.value || null })}
                  className="cursor-pointer rounded-md border border-transparent bg-surface-100 px-2 py-1 text-xs text-surface-700 outline-none dark:bg-surface-800 dark:text-surface-300"
                  title="Model"
                >
                  <option value="">model: default</option>
                  {PROVIDER_MODELS[provider].map((model) => <option key={model} value={model}>{model}</option>)}
                </select>
                <select
                  value={activeSession.reasoningEffort || ''}
                  onChange={(event) => void applyLlmConfig({ reasoningEffort: event.target.value || null })}
                  className="cursor-pointer rounded-md border border-transparent bg-surface-100 px-2 py-1 text-xs text-surface-700 outline-none dark:bg-surface-800 dark:text-surface-300"
                  title="Thinking level"
                >
                  <option value="">thinking: default</option>
                  {PROVIDER_EFFORTS[provider].map((effort) => <option key={effort} value={effort}>{effort}</option>)}
                </select>
              </div>
            )}
          </div>
          <span className="text-xs text-surface-500 dark:text-surface-400">{messages.length} {messages.length === 1 ? 'message' : 'messages'}</span>
        </div>
      )}

      {streamError && (
        <div className="border-b border-destructive-200 bg-destructive-50 px-4 py-2 dark:border-destructive-800 dark:bg-destructive-900/20">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-destructive-600 dark:text-destructive-400" />
              <span className="text-sm text-destructive-700 dark:text-destructive-300">{streamError}</span>
            </div>
            <button onClick={clearStreamError} className="text-xs text-destructive-600 hover:underline dark:text-destructive-400">Dismiss</button>
          </div>
        </div>
      )}

      {activeProviderConnectionRequired && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-900/20">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-700 dark:text-amber-300" />
              <span className="text-sm text-amber-900 dark:text-amber-100">
                {activeProviderConnectionRequired.provider === 'claude' ? 'Claude' : 'Codex'} connection required. Open Status, then select Manage.
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={onOpenStatus}>
                Open Status
              </Button>
              <button
                type="button"
                onClick={clearProviderConnectionRequired}
                className="text-xs text-amber-800 hover:underline dark:text-amber-200"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <div key={activeSessionId ?? 'no-session'} className="chat-pane-transition flex flex-1 flex-col overflow-hidden">
        {!activeSessionId && <Greeting />}
        {activeSessionId && sessionLoading && <LoadingState />}
        {activeSessionId && !sessionLoading && sessionError && <ErrorState error={sessionError} />}
        {activeSessionId && !sessionLoading && !sessionError && (
          <ChatMessageList
            messages={messages}
            isStreaming={isStreaming}
            streamingContent={activeStream?.content || []}
            elapsed={activeStream?.elapsed || 0}
            lastActivityTime={activeStream?.lastActivityTime || null}
            provider={provider}
            readOnlyOwnerName={isReadOnly ? activeSession?.ownerUsername : undefined}
            onSubmitInteraction={onSubmitInteraction}
            onInvokeCommand={isReadOnly ? undefined : handleSendMessage}
          />
        )}
      </div>

      {activeSessionId && !sessionLoading && !sessionError && (
        <ChatFooter
          readOnlyOwnerName={isReadOnly ? activeSession?.ownerUsername : undefined}
          isCopying={isCopying}
          onContinueCopy={() => void handleContinueCopy()}
        >
          <ChatComposer
            onSend={handleSendMessage}
            onCancel={handleCancelStream}
            isStreaming={isStreaming}
            disabled={!activeSession}
            provider={provider}
            skills={skills}
          />
        </ChatFooter>
      )}
    </div>
  );
}

/** The composer, or in another user's chat the read-only banner that replaces it. */
export function ChatFooter({
  readOnlyOwnerName,
  isCopying,
  onContinueCopy,
  children,
}: {
  readOnlyOwnerName: string | undefined;
  isCopying: boolean;
  onContinueCopy: () => void;
  children: ReactNode;
}) {
  if (readOnlyOwnerName === undefined) return <>{children}</>;
  return (
    <ReadOnlyChatBanner ownerUsername={readOnlyOwnerName} isCopying={isCopying} onContinueCopy={onContinueCopy} />
  );
}

export default ChatPanel;
